// weekly planning reports: the backend contract (Plan 1 + Plan 2 hand-offs). Keep in step
// with laravel/code/app/Services/Reports/WeeklyPlanning/{ReportAssembler,RowHelpers}.php.
export type Tier = 'A' | 'B' | 'C';
export type Grain = 'weekly' | 'monthly';
/** which open orders the OTB nets: those due in the window (the rule) or every open PO. */
export type OtbOnOrder = 'window' | 'all';
export type Compare = 'plan' | 'ly' | 'avg';
export type PriceBasis = 'retail' | 'cost';
export type Multiplier = 1.25 | 1.5 | 2;
export type AllocBasis = 'net_sales' | 'gross_margin' | 'cash_margin' | 'inventory';
export type ReportKey = 'inventory_health' | 'plan_otb' | 'weekly_action';
export type RowLevel = 'class' | 'vendor' | 'vendorClass' | 'product' | 'sku' | 'unallocated' | 'unclassified';
// 'mixed': a product row's sales-weighted target over plan classes whose bases differ (targetSource 'weighted').
// 'parent': a child class with no plan of its own under a planned parent;
// no target and no status of its own (summary.planParentKey names the parent that carries them).
export type TargetBasis = 'plan' | 'own_avg' | 'override' | 'none' | 'mixed' | 'parent';
// Sales Trend basis (TrendService::BASIS_*): aggregates whose children differ are 'mixed'. 'lastYear':
// last year's sku_detail daily sales on the same weekdays (364 days earlier), for classes without V3 weights.
export type TrendBasis = 'dailyWeights' | 'lastYear' | 'linear' | 'projected' | 'mixed';
// Which last-year shape a 'lastYear' cell used: its class's own, or the whole store's (class too sparse).
export type TrendNote = 'class' | 'store';
// Why a class with valid V3 weights trended by something else: its plan's daily shape puts no sales on any elapsed
// day (Adam 2026-10-18, e.g. a Thu–Sat-only class early in the week). Absent (or null) when the weights were used.
export type PlanNote = 'planShapeZero';
// every trend method is in one payload. The flat cell fields are 'auto' (= 'plan'); trendAlt carries the
// chains that start at last year (class → store → linear) and days elapsed. Absent on Tier C and outside trend periods.
export interface TrendAltEntry { trendPct: number | null; salesTrend: number | null; trendBasis: TrendBasis | null }
export interface TrendAlt {
  lastYear: TrendAltEntry & { trendNote: TrendNote | null };
  linear: TrendAltEntry;
}
export type TrendShare = { trendPct: number | null; trendBasis: TrendBasis; trendNote?: TrendNote };
export type TargetSource = 'class' | 'weighted' | 'own_avg' | 'none';
// 'review': out of stock with no sales in the lookback nor in the last 52
// weeks (12 months) ending at the client's latest sales; only the payload can say so (it needs the prior-year read).
// 'watch' split into 'watch_low' / 'watch_high' (TargetsService::STATUS_WATCH_LOW / _HIGH).
// 'no_recent_sales' (TargetsService::STATUS_NO_RECENT_SALES): stock on hand and no sales in the lookback
// (it used to read overstocked, WOS "52+"). Never urgent; outside the M1 §6 gate. Deadstock is a flag (Summary.deadstock), not a status.
export type Status = 'on_track' | 'watch_low' | 'watch_high' | 'overstocked' | 'understocked' | 'out_of_stock' | 'review' | 'no_recent_sales' | 'no_plan';
// Where a SKU / product row's suggestedReorderUnits come from (TargetsService::REORDER_*): the lookback average, the last
// 52 weeks (no sales in the lookback), or 'mixed' on a product whose variants differ. Null without units; never on class / vendor rows.
export type ReorderBasis = 'lookback' | 'last52' | 'mixed';
export type OtbBasis = 'weekly_plan' | 'weekly_plan_partial' | 'monthly_plan' | 'cost_unavailable' | 'receipts_unavailable' | 'mixed' | null;

export interface Capabilities {
  clientId: number;
  tier: Tier | null;
  hasDailyActuals: boolean;
  hasItemLevelDaily: boolean;
  hasV3Plans: boolean;
  weeklyMode: boolean;
  skuLevel: boolean;
  hasReceiving: boolean;
  skuDetailSources: string | null;
  actualsSource: string;
  planSource: string;
  degraded: boolean;
  /** Tiers A/B: the last sales day (Sales Trend's salesThrough); null for Tier C or no sales in 90 days. */
  salesThrough?: string | null;
  /** none | location | class; absent (older payloads, pending) = none. */
  benchmarkLevel?: BenchmarkLevel;
  /** W3: the fields /product-search can match for this client's POS (absent without SKU-level data or in older payloads). */
  searchFields?: ProductSearchField[];
}

/** W3: a field the product search can match. Which ones a client has depends on its POS (Shopify: all four; Heartland: no tags; others: name and SKU). */
export type ProductSearchField = 'title' | 'sku' | 'barcode' | 'tags';
/** A class + vendor a matching product sits under: load its rows with product-rows / sku-rows under these parents. */
export interface ProductSearchParent { locationClassId: number; vendor: string; vendorKey: string }
export interface ProductSearchProduct {
  productId: string;
  title: string;
  /** The field that matched (identifiers before names before tags) and its value (the SKU, barcode or the one tag; the title for a name match). */
  matchedField: ProductSearchField | null;
  matchedValue: string | null;
  /** The matching variants (SKU / barcode matches name the variant; a name or tag match lists the product's variants). */
  variantIds: string[];
  parents: ProductSearchParent[];
  /** Sales over the lookback, which ranks the matches. */
  sales: number;
}
export interface ProductSearchResult {
  q: string;
  /** What was searched for this client; the search box names only these. */
  fields: ProductSearchField[];
  /** Every product that matches; `products` holds the best `limit` of them. */
  total: number;
  limit: number;
  truncated: boolean;
  products: ProductSearchProduct[];
  fromCache?: boolean;
}

/** which benchmark level the client may see (BenchmarkEntitlement). */
export type BenchmarkLevel = 'none' | 'location' | 'class';
export type BenchmarkMetricId = 'sellThrough' | 'weeksOfStock' | 'turnover' | 'stockToSales' | 'markdownPct' | 'grossMarginPct' | 'cashMarginPct' | 'salesChangeLy';
/** BenchmarkService statuses; only a fallback month carries a notice, the browser derives text for the rest from the status. */
export type BenchmarkStatus = 'ok' | 'not_entitled' | 'month_out_of_window' | 'no_client_rows' | 'no_vertical' | 'unavailable';
export interface BenchmarkSums {
  salesRetail: number; salesCost: number; salesCosted: number; markdowns: number; inventoryBom: number; inventoryEom: number;
  inflowRetailCovered: number; inflowCostCovered: number; salesWithInflow: number; salesRetailPrior: number; monthRows: number; priorRows: number;
}
export type BenchmarkMetrics = Record<BenchmarkMetricId, number | null>;
/** A peer pool: never a number when !publishable (gated in SQL). */
export interface BenchmarkPeer { publishable: boolean; inflowPublishable: boolean; peerClients: number | null; metrics: BenchmarkMetrics }
export interface BenchmarkEntity { sums: BenchmarkSums; metrics: BenchmarkMetrics; peer: BenchmarkPeer }
/**
 * GET /benchmarks (BenchmarkService::forClient). Every status has this one shape. PHP returns [] for empty maps, so
 * locations, classes, byMasterClass and peers.masterClass may arrive as []: read them through Object.entries / obj[key]
 * only (both treat [] as empty, as TrendBlock's maps do). The payload is not cell-compacted (no expandCompactCells).
 */
export interface BenchmarkPayload {
  clientId: number; level: BenchmarkLevel; status: BenchmarkStatus;
  month: number | null; monthLabel: string | null; requestedMonth: number | null; latestMonth: number | null; windowFirstMonth: number | null;
  isFallback: boolean; vertical: string | null; weeksInMonth: number;
  client: BenchmarkEntity | null;
  locations: Record<string, BenchmarkEntity & { byMasterClass: Record<string, BenchmarkEntity> }>;
  classes: Record<string, { locationId: number | null; masterClass: string | null; sums: BenchmarkSums }>;
  peers: { vertical: BenchmarkPeer | null; masterClass: Record<string, BenchmarkPeer> };
  notices: string[]; errors: PartError[];
}

export interface ReportParams {
  grain: Grain;
  target: number;
  lookback: number;
  compare: Compare;
  /** OTB span follows the grain: Monthly sends otbSpanMonths (0..11), Weekly otbSpanWeeks (1..52; the control offers 4 / 8 / 13 / 26); the backend defaults the grain's own (3 months, 13 weeks) and ignores the other. */
  otbSpanMonths?: number;
  otbSpanWeeks?: number;
  /**
   *: the epoch period (the request's grain) the span starts at, at least the target and at most
   * 26 weeks / 12 months after it; absent = the target. The payload's `spanPeriods` reflect it; a future window makes `summary.otbDev` /
   * `deviation` null with a notice.
   */
  otbStart?: number;
  /** 'all': every open PO whenever due, overdue included, nets the OTB; absent ('window') = on order due inside the window, the rule. */
  otbOnOrder?: 'all';
  /** Display history (max 6): extra past periods before the target, for Plan & OTB's "Show previous 4". Never changes the lookback behind targets and WOS. Absent = none. */
  history?: number;
  basis: PriceBasis;
  /** no longer sent by the report (the browser applies it); the backend still accepts it and defaults to 1.5. */
  multiplier?: Multiplier;
  /** product rows only: rank (and cap) the products by 'inventory' (Top by inventory); absent = by sales. */
  sort?: 'inventory';
}
export interface VendorRowsParams extends ReportParams {
  allocWindow: number;
  allocBasis: AllocBasis;
}

export interface Cell {
  sales: number | null; unitsSold: number | null; markdowns: number | null;
  inflow: number | null; inflowCost: number | null;
  inventoryStart: number | null; inventoryEnd: number | null;
  salesPlan: number | null; inflowPlan: number | null; inventoryPlan: number | null; markdownsPlan: number | null;
  /**
   * the plan's own inflow at cost (Monthly: V3 inflow_cost_plan, ReportDataSource inflowPlanCost; a vendor row's
   * share of it). Null in Weekly (V3 weekly plans carry no cost) and on SKU / product rows. Read through kit/logic/basis.ts.
   */
  inflowCostPlan?: number | null;
  isEstimated: boolean; isCurrent: boolean; isAllocated: boolean; planRolledUp: boolean; isProjected: boolean;
  /** D6 (Monthly class rows): the plan in this cell is the V2 plan as it stood in the month, used because the month is before the class's first V3 month. */
  planV2History?: boolean;
  // vendor / SKU / Unallocated rows only (absent on class rows)
  unitsStart?: number | null; unitsEnd?: number | null; isFloored?: boolean;
  /**
   * Inventory at cost. Vendor / SKU rows: units × cost per unit. class rows too (Tiers A/B: the facts'
   * InventoryCost month-ends and today's cost on hand; Weekly back-calculated with inflowCost − cogs); null on Tier C.
   */
  inventoryCostStart?: number | null; inventoryCostEnd?: number | null;
  salesLy: number | null; markdownsLy: number | null; inflowLy: number | null; inventoryLy: number | null; lyPartial: boolean;
  /** no LY BOM because the client has no facts row for LY month M−1 (first month of history); inventory only, lyPartial untouched. */
  inventoryLyMissing?: boolean;
  // Sales Trend: set on a trend period's cells (the period containing salesThrough, and later started periods); null elsewhere.
  // Always read salesTrend from the cell: an Unallocated row's is a remainder, not sales ÷ trendPct.
  trendPct?: number | null; salesTrend?: number | null; trendBasis?: TrendBasis | null;
  /** Only on 'lastYear' cells (absent otherwise); null on an aggregate whose children used different shapes. */
  trendNote?: TrendNote | null;
  /** 'planShapeZero' when valid weights had no elapsed sales and the chain fell past them; null on an aggregate whose children differ; absent otherwise. */
  planNote?: PlanNote | null;
  /** the lastYear and linear methods' values (same rules as the flat fields); absent on Tier C / non-trend cells. */
  trendAlt?: TrendAlt;
}

/**
 * (ReportAssembler / OtbByPeriod): one period of a class's OTB span, in the table's grain (OTB span follows
 * the grain: an epoch week in Weekly, an epoch month in Monthly). otb = inflowPlan − received − onOrderDue; running = cumulative otb,
 * and the last line's running = summary.otb (M1: running OTB governs spendability; the per-period figures are not independent
 * budgets). Retail only (no per-period figure at cost).
 */
export interface OtbPeriodLine { period: number; inflowPlan: number; received: number; onOrderDue: number; otb: number; running: number }

export interface Summary {
  inventory: number | null; inventoryKnown: boolean; avgPeriodSales: number | null;
  wos: number | null; targetWos: number | null; targetBasis: TargetBasis; status: Status | null; urgent: boolean;
  onOrder: number | null; onOrderCost: number | null; receivedToDate: number | null; inflowPlanSpan: number | null;
  otb: number | null; otbBasis: OtbBasis; otbCoveredThrough: number | null;
  // vendor / vendorClass / SKU rows only; SKU and product rows also carry the two unit fields and the reorder basis
  targetSource?: TargetSource; avgPeriodUnits?: number | null; suggestedReorderUnits?: number | null; reorderBasis?: ReorderBasis | null;
  /**
   * (class rows only, ReportAssembler::deferChildrenToPlannedParent): set on a child class with no plan of its own
   * under a planned parent — the parent's row key and "code · name". Its status is null and its target 'parent'; absent otherwise.
   */
  planParentKey?: string | null; planParentLabel?: string | null;
  /**
   * (Adam's D1; ReportAssembler::classSummary, RowHelpers::summary): the first span period's plan and actual
   * BOM (retail), deviation = plan BOM − actual BOM (negative when the period starts over plan), otbDev = otb + deviation.
   * Null when an input is unknown. A vendor × class row carries its shares of the class's (they sum to the class), a vendor
   * row its children's. Optional so older payloads and fixtures read as unknown (the service fills them with null).
   */
  spanBomPlan?: number | null; spanBomActual?: number | null; deviation?: number | null; otbDev?: number | null;
  /** pet-store column (browser only, kit/logic/velocity.ts): the period in progress's trended OTB; stamped on class rows, summed by the row tree. */
  trendedOtb?: number | null;
  /** The on order OTB nets (summary.onOrder stays the cover / urgency figure): inflowPlanSpan − receivedToDate − otbOnOrder = otb. */
  otbOnOrder?: number | null;
  /**
   * Merged rows (chain rows, subtotals): the OTB's own received / on order, summed over the members that HAVE an OTB only, so
   * inflowPlanSpan − otbReceived − otbOnOrder = otb holds on the row. receivedToDate / onOrder stay the full actuals. Absent =
   * the plain fields are the OTB inputs. onOrderNoOtb: the on order of the members without an OTB (the card's "on classes without
   * a plan" clause); the Cost twins are the same at cost.
   */
  otbReceived?: number | null; otbReceivedCost?: number | null; onOrderNoOtb?: number | null; onOrderNoOtbCost?: number | null;
  /** OTB by month and running OTB (class rows with an OTB; null elsewhere, and on vendor rows). */
  otbByPeriod?: OtbPeriodLine[] | null;
  /**
   * (ReportAssembler::classSummary): OTB at cost = inflowCostPlanSpan − receivedCostToDate − otbOnOrderCost, all
   * at cost from the data (never retail × a markup). Class rows in Monthly only (V3 weekly plans carry no inflow at cost);
   * null on vendor / SKU / product rows and wherever retail OTB is null. Every other summary field is retail on both bases.
   */
  inflowCostPlanSpan?: number | null; receivedCostToDate?: number | null; otbOnOrderCost?: number | null; otbCost?: number | null;
  /**
   * D5: the planned classes whose weekly plan ends before the span (key, "code · name", last planned week). On a
   * parent whose plan is its children's it lists each partial child; otbCoveredThrough is then the earliest end.
   */
  otbCoverage?: { key: string; label: string; through: number }[];
  /** 'PNOTB' / 'NOTB' (M1 §8): the class is not bought against an OTB, so otb and otbDev are null and every OTB total leaves it out. */
  otbExcluded?: string | null;
  /**
   * D4 (Weekly only): 'none_weekly' = the class has no weekly plan (or the client may not show weekly plans): plan cells,
   * OTB and OTB + Dev are null and read "No weekly plan"; its target is its own average. Null otherwise (also Monthly).
   * (Monthly): 'none_plan' = no plan is loaded for the period (ReportAssembler::markNoPlanLoaded; sources.plans
   * 'none_plan'): plan cells, OTB, OTB + Dev and the deviation are null and read "—", never OTB = −on order.
   */
  planBasis?: 'none_weekly' | 'none_plan' | null;
  /**
   * the class's effective service type (a Child takes its ancestor's; ReportAssembler::applyActions), on class,
   * vendor and SKU rows; and, on class rows, the backend's action (TargetsService::action; targets.ts `ActionCode`). The browser
   * re-derives the action with `rowAction`, so `action` is informational.
   */
  serviceTypeId?: number | null;
  action?: 'reorder' | 'reorder_no_otb' | 'on_order_covers' | 'out_of_season' | 'class_type' | 'sell_down' | 'push_orders' | 'sell_through' | 'hold' | 'review' | 'review_clear' | null;
  /**
   * review (class rows, Tiers A/B; ReportAssembler::onOrderDueMaps): retail on order by the period it is due in
   * (overdue in the current period), so "On order covers it" and urgency count only what lands within the target horizon.
   * Absent: the whole span's on order counts.
   */
  onOrderDue?: Record<string, number> | null;
  /**
   * W2 (vendor × class rows of /vendor-rows, Tiers A/B; VendorRowsAssembler): retail on order due by the end of the
   * target period (a PO line with no delivery date is due today, overdue included), for the Ship / hold hint. Null when that read
   * failed; absent on older payloads and other rows.
   */
  onOrderDueThisPeriod?: number | null;
  /**
   * M1 §6 (TargetsService::deadstock; rowTree re-derives it on subtotals and chain rows): more than a year
   * of supply at the current sell rate (52 weeks / 12 months; no sales is unbounded) AND sales over the lookback below last year's.
   * A flag beside the status, never a replacement. Optional: older payloads read as not flagged.
   */
  deadstock?: boolean;
  /**
   * Client-side only (rowTree subtotals and chain rows): Σ plan BOM of the members that have one, and Σ those members'
   * inventory — the pair the M1 §6 gate compares (targets.ts gateOf). Null when no member has a plan BOM.
   */
  planBom?: number | null; planBomInventory?: number | null;
  /**
   * Round-2 D7 (ReportAssembler::markNonSelling): the row is a non-selling location (Warehouse, Receiving, HQ, Storage, "To Be
   * Split", Event with no sales in the window): no status, target, urgency or deadstock, action "—" with `nonSellingNote`. Its
   * inventory, receipts and on order stay on the row: chain and class totals sum them and net them against the chain OTB (rowTree).
   */
  nonSelling?: boolean; nonSellingNote?: string | null;
  /**
   * Round-2 D9 (SuspectData): why the row's numbers look wrong, kept as they are (flag only). Codes: 'sku_outlier',
   * 'mirrored_inventory', 'tiny_plan', 'unallocated_share'. Absent / empty = nothing suspect.
   */
  dataFlags?: DataFlag[];
}

export type DataFlag = 'sku_outlier' | 'mirrored_inventory' | 'tiny_plan' | 'unallocated_share';

export interface Freshness {
  onHandUnits: number; onHandRetail: number; freshUnits: number; freshRetail: number;
  aging4wUnits: number; aging4wRetail: number; aging8wUnits: number; aging8wRetail: number; aging12wUnits: number; aging12wRetail: number;
  freshPctSku: number | null; aging4wPct: number | null; aging8wPct: number | null; aging12wPct: number | null;
  /** Browser roll-ups only: some members had no aging block, so the shares cover only the members that have one. */
  partial?: boolean;
}

/**
 * W2 (allocation, "open to sell"; kit/logic/allocation.ts, PHP Allocation): client-side only, never sent by the API.
 * Retail dollars for the target period. On a class row of Class › Location (and every chain class row): Σ its stores' need, Σ its
 * non-selling locations' on hand (`available`, null with none), `balance` = need − available (> 0 shortfall, < 0 surplus) and the
 * greedy split (`stores`, ranked by need; `sources`). On a store's location row: `allocate`, what to send it and from where.
 */
export interface StoreAllocation { key: string; label: string; need: number; allocate: number; from: string[] }
export interface SourceAllocation { key: string; label: string; available: number; allocated: number }
export interface RowAllocation {
  need: number | null; available: number | null; balance: number | null;
  stores: StoreAllocation[]; sources: SourceAllocation[];
  /** Σ the non-selling locations' BOM at cost (null when one has no cost figure, or with none); browser only, for the cost basis. */
  availableCost?: number | null;
  /** Σ the stores' gross need (before on order), what the ship / hold hint reads; browser only. */
  grossNeed?: number | null;
}
/** Item 12 (display hint only): a vendor × class row's on order due in the period against its class's gross need there less the other vendors' due. */
export interface ShipHint { hint: 'hold' | 'take'; due: number; need: number; otherDue?: number }

interface RowBase {
  key: string;
  locationClassId: number | null;
  isUnallocated: boolean;
  classCode: string | null; className: string | null; category: string | null; categoryId: number | null;
  locationId: number | null; locationCode: string | null; locationName: string | null;
  onOrderRetail: number | null; onOrderCost: number | null; onOrderUnits: number | null;
  periods: Record<string, Cell>;
  summary: Summary | null;
  /** W2, client-side only (rowTree): see RowAllocation. */
  allocation?: RowAllocation;
  /** W2, client-side only (rowTree): on a store's location row in Class › Location, what to send it. */
  allocate?: StoreAllocation;
}
export interface ClassRow extends RowBase {
  parentLocationClassId: number | null;
  isParent: boolean;
  /** ServiceType id (App/Models/ServiceType.php) from the class dimension (1 OTB, 2 PNOTB, 3 NOTB, 10 Child…); null on Unclassified.. */
  serviceTypeId?: number | null;
  /**
   * the class's aging
   * buckets rolled up from its SKUs (FreshnessService::classRollup: bucket dollars ÷ class inventory, parents sum their children).
   * Absent on other reports and on a class with no SKU stock.
   */
  freshness?: Freshness | null;
  /**
   * Client-side only (rowTree's chain roll-up): the number of stores a chain row (`chain:<class code>`) sums in
   * Total / Category by Class. Absent on payload rows.
   */
  chainStores?: number;
  /** Round-2 D7: a non-selling location's class row (its summary carries `nonSelling`). */
  isNonSelling?: boolean;
  /**
   * W2, client-side only: the row tree sets `'location'` on a store's (or non-selling location's) class row it places under
   * its class in Class › Location (and › Vendor), so the label column reads the location instead of repeating the class.
   */
  labelAs?: 'location';
}
export interface VendorRow extends RowBase {
  level: 'vendor' | 'vendorClass' | 'product' | 'sku' | 'unallocated';
  parentKey: string | null;
  vendor: string; vendorKey: string;
  planClassId: number | null;
  freshness: Freshness | null;
  /** Round-2: the vendorClass rows of the "Unallocated / adjustments" vendor: class inventory − Σ vendors (signed). */
  isInventoryRemainder?: boolean;
  variantId?: string; skuId?: string | null; productId?: string | null; productTitle?: string | null; color?: string | null; size?: string | null; variantCount?: number;
  /**
   * Client-side label hint, never sent by the API: the kit's row tree sets `'vendor'` on a vendorClass row it places
   * under its CLASS (Class by Vendor), so the label column reads the vendor ("Acme") instead of repeating the class;
   * under its vendor (Vendor by Class) the row keeps the class label.
   */
  labelAs?: 'vendor';
  /** W2 item 12, client-side only (rowTree): Hold / Take for a vendor × class row with on order due in the period. */
  shipHint?: ShipHint;
}
export type AnyRow = ClassRow | VendorRow;
export const isVendorRow = (r: AnyRow): r is VendorRow => 'level' in r;
export const levelOf = (r: AnyRow): RowLevel => isVendorRow(r) ? r.level : (r.key === 'unclassified' ? 'unclassified' : (r.isUnallocated ? 'unallocated' : 'class'));

export interface PartError { part: string; message: string }
export interface Sources { actuals?: string | null; plans?: string | null; planVersion?: number | null; planVersionsMixed?: boolean; /** D6: the client's first V3 monthly-plan epoch month, when Monthly months before it carry the V2 plan as it stood. */ planV2HistoryBefore?: number | null; liveVersionResolved?: boolean; inventory?: string | null; onOrder?: string | null; lastYear?: string | null; allocation?: string | null }
export interface LastYearBlock { periods: Record<string, number>; periodStart: Record<string, string>; historyStart: string | null }

// The payload's Sales Trend block (TrendService::block); null when there is no trend period.
// Period-keyed maps arrive as JSON objects keyed by the period number, or as [] when PHP had none.
export interface TrendBlock {
  periods: number[];
  salesThrough: string;
  fallbackBasis: 'linear' | 'projected';
  linear: Record<string, number>;
  /** Tier C: the projected share of the period containing salesThrough; null for Tiers A/B. */
  projectedPct: number | null;
  /** Tier C: the projected share per trend period; empty for Tiers A/B. */
  projected: Record<string, number>;
  byClass: Record<string, Record<string, { trendPct: number | null; trendBasis: TrendBasis; trendNote?: TrendNote }>>;
  /** Per trend period, what a leaf with no class share uses (store last-year shape, else linear / projected). Absent on older payloads. */
  fallback?: Record<string, { trendPct: number | null; trendBasis: TrendBasis; trendNote?: TrendNote }>;
  /** (Tiers A/B): byClass for the lastYear and linear methods. Absent on Tier C and older payloads. */
  byClassAlt?: Record<keyof TrendAlt, Record<string, Record<string, TrendShare>>>;
  /** (Tiers A/B): fallback for the lastYear and linear methods. */
  fallbackAlt?: Record<keyof TrendAlt, Record<string, TrendShare>>;
}

export interface ReportPayloadBase<R> {
  report: string; clientId: number; capabilities: Capabilities; params: Record<string, unknown>;
  unsupported?: boolean; grain: Grain; periods: number[]; currentPeriod: number; targetPeriod: number;
  lookbackPeriods: number[]; spanPeriods: number[]; spanEndDate: string | null; dataThrough: string | null;
  /** / the OTB span's periods in the grain (epoch weeks in Weekly, epoch months in Monthly; the first is the current one): `summary.otbByPeriod` has one line each. */
  otbPeriods?: number[];
  /** Last SALES day the trend counts through (Tier C: the RDS dataThrough); dataThrough stays inventory freshness. */
  salesThrough?: string | null;
  sources: Sources; trend?: TrendBlock | null; notices: string[]; errors: PartError[]; degraded: boolean; fromCache: boolean; rows: R[];
  /**
   * Wire-only (CellCompactor.php): true when the cells omit their null / false keys. WeeklyPlanningReports.service
   * restores every key (expandCompactCells) and drops the flag, so nothing past the service ever sees it set.
   */
  cellsCompact?: boolean;
}
export interface ClassReportPayload extends ReportPayloadBase<ClassRow> {
  lastYear?: LastYearBlock | null;
  /**
   * Round-2 (4830; ReportAssembler / InventoryService::incompleteSnapshot): `incomplete` is true when today's inventory snapshot is under
   * 25% of the facts' last month-end inventory, so weekly (and every vendor / SKU) inventory is unknown and a notice says why.
   */
  inventorySnapshot?: { incomplete: boolean; snapshot?: number; facts?: number; coverage?: number };
}
export type VendorRowsPayload = ReportPayloadBase<VendorRow>;
export interface SkuRowsPayload extends ReportPayloadBase<VendorRow> { parent: string }
// /product-rows: parent is the canonical scope the server echoes (all | class:<id> | classes:<sorted ids> |
// category:<id|none> | vendor:<key>). Rows: level 'product' (key product:<id> or product:none) each followed
// by its variant rows (level 'sku', parentKey = the product's key); a single-class scope ends with the
// level 'unallocated' row keyed <parent>:unallocated. productLimit is the cap that applied (500 for all, 2,000 for a filtered scope).
export interface ProductRowsPayload extends ReportPayloadBase<VendorRow> { parent: string; truncated: boolean; totalProducts: number; productLimit: number }
